import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Field as BaseField } from "rc-field-form";
import moment from "moment-timezone";
import Modal from "../../../../../../components/Modal";
import Field from "../../../../../../components/Field";
import Button from "../../../../../../components/Button";
import Select from "../../../../../../components/Inputs/Select";

const FORMAT_OPTIONS = {
    date: ["YYYY/MM/DD", "YYYY-MM-DD", "MM/DD/YYYY", "MM/DD/YYYY MMM"],
    "date-time": ["MM/DD/YYYY HH:mm", "DD/MM/YYYY HH:mm", "YYYY-MM-DD HH:mm", "MM/DD/YYYY h:mm a"],
    timestamp: ["MM/DD/YYYY HH:mm", "DD/MM/YYYY HH:mm", "YYYY-MM-DD HH:mm", "MM/DD/YYYY h:mm a"],
    datepicker: ["YYYY/MM/DD", "YYYY-MM-DD", "MM/DD/YYYY", "MM/DD/YYYY MMM"],
    time: ["HH:mm", "HH:mm:ss", "hh:mm a", "hh:mm:ss a"],
    secondstotime: ["HH:mm", "HH:mm:ss", "decimal"],
    integer: ["plain", "comma", "dot"],
    float: ["plain", "comma", "dot"],
    money: ["currency", "comma", "plain"],
    string: ["uppercase", "lowercase", "capitalize"],
    select: ["uppercase", "lowercase", "capitalize"],
    "resource-select": ["uppercase", "lowercase", "capitalize"],
    country: ["uppercase", "lowercase", "capitalize"],
    boolean: ["true-false", "yes-no", "1-0"],
};

const NUMBER_LABELS = {
    integer: { plain: "1000", comma: "1,000", dot: "1.000" },
    float: { plain: "1000.00", comma: "1,000.00", dot: "1.000,00" },
    money: { currency: "$1,000.00", comma: "1,000.00", plain: "1000" },
};

function getFormatLabel(type, format, t) {
    if (["date", "date-time", "datepicker", "timestamp", "time", "secondstotime"].includes(type)) {
        return format === "decimal" ? "8.50" : moment().format(format);
    }
    if (["string", "select", "resource-select", "country"].includes(type)) {
        const sample = t("sample");
        if (format === "uppercase") return sample.toUpperCase();
        if (format === "lowercase") return sample.toLowerCase();
        if (format === "capitalize") return sample.charAt(0).toUpperCase() + sample.slice(1).toLowerCase();
        return format;
    }
    if (type === "boolean") {
        return t(format);
    }
    if (["integer", "float", "money"].includes(type)) {
        return NUMBER_LABELS[type]?.[format] ?? format;
    }
    return format;
}

const resolveFieldType = (field) => {
    if (field?.fields?.length) {
        return resolveFieldType(field.fields[0]);
    }
    return (field?.formatType || field?.fieldType || field?.type || "").toLowerCase();
};

function Configuration({ visible, close, selected, setFormData, form }) {
    const { t } = useTranslation();
    const [initialValues, setInitialValues] = useState(null);

    useEffect(() => {
        if (!visible) {
            setInitialValues(null);
            return;
        }
        if (initialValues) return;
        const config = selected?.configuration || {};
        const configuration = config ?? form.getFieldValue(["dimensions", selected?.name, "configuration"]);
        form.setFieldValue(["dimensions", selected?.name, "configuration"], configuration);
        setInitialValues(configuration);
    }, [visible, initialValues, form, selected]);

    const getFieldOptions = useCallback((field) => {
        const name = field?.name?.split(".")?.[1];
        const selectedField = selected?.selectedFields?.find(
            (item) => item?.name === name
        );
        const type = resolveFieldType(selectedField);

        const options = ["-", "empty"];
        if (["integer", "float", "money"].includes(type)) {
            options.push("0");
        }

        return options;
    }, [selected]);

    const getFormatOptions = useCallback((field) => {
        const name = field?.name?.split(".")?.[1];
        const selectedField = selected?.selectedFields?.find(
            (item) => item?.name === name
        );
        const type = resolveFieldType(selectedField);
        return (FORMAT_OPTIONS[type] || []).map((value) => ({
            value,
            label: getFormatLabel(type, value, t),
        }));
    }, [selected, t]);

    const cancel = useCallback(() => {
        form.setFieldValue(["dimensions", selected?.name, "configuration", "emptyState"], initialValues?.emptyState);
        form.setFieldValue(["dimensions", selected?.name, "configuration", "format"], initialValues?.format);
        close();
    }, [close, form, initialValues?.emptyState, initialValues?.format, selected?.name]);

    const save = useCallback(() => {
        const emptyState = form.getFieldValue(["dimensions", selected?.name, "configuration", "emptyState"]);
        const format = form.getFieldValue(["dimensions", selected?.name, "configuration", "format"]);
        setFormData(prev => ({
            ...prev,
            dimensions: prev?.dimensions?.map((dimension, index) =>
                index === selected?.name
                    ? { ...dimension, configuration: { emptyState, format } }
                    : dimension
            )
        }));
        close();
    }, [close, form, selected?.name, setFormData]);

    return (
        <Modal
            title={t("configuration")}
            isOpen={visible}
            close={close}
            centered
        >
            <BaseField shouldUpdate>
                {({ }, { }, { getFieldValue }) => {
                    const field = getFieldValue(["dimensions", selected?.name]);
                    const options = getFieldOptions(field);

                    return (
                        <Field
                            label={t("empty-state")}
                            name={[selected?.name, "configuration", "emptyState"]}
                        >
                            <Select placeholder={t("empty-state")}>
                                {options.map((option) => (
                                    <Select.Option
                                        key={option}
                                        value={option}
                                    >
                                        {t(option)}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Field>
                    );
                }}
            </BaseField>

            <BaseField shouldUpdate>
                {({ }, { }, { getFieldValue }) => {
                    const field = getFieldValue(["dimensions", selected?.name]);
                    const formatOptions = getFormatOptions(field);

                    return (
                        <Field
                            label={t("value-format")}
                            name={[selected?.name, "configuration", "format"]}
                        >
                            <Select placeholder={t("value-format")}>
                                {formatOptions.map(({ value, label }) => (
                                    <Select.Option
                                        key={value}
                                        value={value}
                                    >
                                        {label}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Field>
                    );
                }}
            </BaseField>

            <div className="d-flex justify-content-end">
                <Button
                    color="muted"
                    type="button"
                    onClick={cancel}
                >
                    {t("cancel")}
                </Button>

                <Button
                    color="primary"
                    type="button"
                    onClick={save}
                >
                    {t("save")}
                </Button>
            </div>
        </Modal>
    );
}

export default Configuration;
